import { Injectable } from '@angular/core';
import { AbstractService } from './abstract-service.service';
import {
    FormGroup,
    AbstractControl,
    FormControl,
    Validators,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    ApprovalState,
    ITimesheetFormControls,
} from '../interfaces/timesheets';
import { TimeSheet } from '../models/timesheet.model';
import { notesRequiredCheck } from '../validators/notesRequired.validator';
import {
    daysOfWeek,
    notesCharacterLimit,
} from '../constants/timesheetConstants';
import { Day } from '../types/dayType';
import { invalidDecimalCheck } from '../validators/decimalCheck.validator';

@Injectable({
    providedIn: 'root',
})
export class TimesheetService extends AbstractService {
    path = '/timesheet';

    public dayControls = (timesheet: TimeSheet = null) =>
        daysOfWeek.reduce(
            (formControls, day) => {
                formControls[day.name] = new FormControl<number>(
                    timesheet !== null ? timesheet[day.name] : 0,
                    [
                        Validators.max(24),
                        Validators.min(0),
                        invalidDecimalCheck(),
                        notesRequiredCheck(day.name),
                    ],
                );

                formControls[`${day.name}Notes`] = new FormControl<string>(
                    timesheet !== null ? timesheet[day.name + 'Notes'] : '',
                    [Validators.maxLength(notesCharacterLimit)],
                );

                return formControls;
            },
            {} as Record<Day | `${Day}Notes`, FormControl>,
        );

    getTimesheets(
        route: string[],
        queryParameters?: Map<string, number>,
    ): Observable<FormGroup<ITimesheetFormControls>[]> {
        let url = this.baseUrl + this.path;

        url +=
            this._buildRouteFromStringArray(route) +
            this._buildQueryParameters(queryParameters);

        return this.httpClient
            .get(url)
            .pipe(map((t: TimeSheet[]) => this.createTimesheetFormGroups(t)));
    }

    public createTimesheetFormGroups(
        timesheets: TimeSheet[],
    ): FormGroup<ITimesheetFormControls>[] {
        const timesheetFormGroups: FormGroup<ITimesheetFormControls>[] = [];

        timesheets.forEach((timesheet: TimeSheet) => {
            const timesheetForm = new FormGroup<ITimesheetFormControls>({
                id: new FormControl<string>(timesheet.id),
                projectId: new FormControl<string>(timesheet.projectId),
                taskId: new FormControl<string>(timesheet.taskId),
                week: new FormControl<number>(timesheet.week),
                year: new FormControl<number>(timesheet.year),
                ...this.dayControls(timesheet),
                taskName: new FormControl<string>(timesheet.taskName),
                usersTasksId: new FormControl<string>(timesheet.usersTasksId),
                clientName: new FormControl<string>(timesheet.clientName),
                userProfileId: new FormControl<string>(timesheet.userProfileId),
                firstName: new FormControl<string>(timesheet.firstName),
                lastName: new FormControl<string>(timesheet.lastName),
                projectName: new FormControl<string>(timesheet.projectName),
                submitted: new FormControl<boolean>(timesheet.submitted),
                locked: new FormControl<boolean>(timesheet.locked),
                saved: new FormControl<boolean>(timesheet.saved),
                approvalState: new FormControl<ApprovalState>(
                    timesheet.approvalState,
                ),
                notesRequired: new FormControl<boolean>(
                    timesheet.notesRequired,
                ),
            });
            timesheetForm.markAllAsTouched();
            timesheetFormGroups.push(timesheetForm);
        });

        return timesheetFormGroups;
    }

    public hasNotes(
        day: Day,
        timesheet: FormGroup<ITimesheetFormControls>,
    ): boolean {
        return Boolean(
            (timesheet.controls[day + 'Notes'].value ?? '').length > 0,
        );
    }
}
